import * as THREE from "three";
import * as React from "react";
import { useThree } from "@react-three/fiber";

// Mounted only in ?shadows=live mode. Exposes window.__exportShadowMap(),
// which copies the AccumulativeShadows lightmap (a HalfFloat render target)
// into an 8-bit target at HALF scale (headroom for values > 1) and returns
// it as a PNG data URL. scripts/bake-shadows.mjs drives this from Playwright
// and writes the result to public/textures/baked-shadows.png, which
// BakedShadows.jsx replays at runtime.
export default function ShadowBakeExporter({ shadowsRef }) {
  const gl = useThree((s) => s.gl);

  React.useEffect(() => {
    window.__exportShadowMap = () => {
      const mesh = shadowsRef.current?.getMesh?.();
      const tex = mesh?.material?.map;
      if (!tex) throw new Error("AccumulativeShadows lightmap not found");

      const res = tex.image?.width || 1024;
      const rt = new THREE.WebGLRenderTarget(res, res, { depthBuffer: false });
      const scene = new THREE.Scene();
      const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      const mat = new THREE.ShaderMaterial({
        uniforms: { map: { value: tex } },
        vertexShader: `varying vec2 vUv;
          void main() { vUv = uv; gl_Position = vec4(position.xy, 0., 1.); }`,
        fragmentShader: `varying vec2 vUv; uniform sampler2D map;
          void main() { gl_FragColor = vec4(texture2D(map, vUv).rgb * 0.5, 1.0); }`,
      });
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat));

      const prev = gl.getRenderTarget();
      gl.setRenderTarget(rt);
      gl.render(scene, cam);
      const px = new Uint8Array(res * res * 4);
      gl.readRenderTargetPixels(rt, 0, 0, res, res, px);
      gl.setRenderTarget(prev);
      rt.dispose();
      mat.dispose();

      const canvas = document.createElement("canvas");
      canvas.width = canvas.height = res;
      const ctx = canvas.getContext("2d");
      const img = ctx.createImageData(res, res);
      img.data.set(px);
      ctx.putImageData(img, 0, 0);
      return canvas.toDataURL("image/png");
    };
    return () => {
      delete window.__exportShadowMap;
    };
  }, [gl, shadowsRef]);

  return null;
}
